import { useEffect, useState } from "react";
import { Box, Button, Checkbox, IconButton, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TablePagination, TableRow, Typography } from "@mui/material";
import { COLOR } from "../../../utils/colors";
import { defaultHeaderStyle, defaultTableCellStyle } from "../../robots/components/v2/_style";
import { LocationMapType, LocationType } from "../../../hooks/location/index.type";
import EditIcon from "../../../components/icons/EditIcon";
import { useListConfigWaypoints } from "../../../hooks/config-page/waypoint";
import { useDebounce } from "../../../utils/debounce";
import { useQueryState } from "nuqs";
import { decryptText } from "../../../utils/encryption";



const columns = ['Name', 'Building', 'Level', 'Point Type', 'Map', 'Action']

export default function ListLocationTable({
  search,
  showNextButton = false,
  onNext,
  onEdit
}: {
  search?: string
  showNextButton?: boolean
  onNext?: () => void
  onEdit?: (location?: LocationType) => void
}) {
  const [building_uuid] = useQueryState('building_uuid', { clearOnDefault: true })
  const searchQuery = useDebounce(search, 300)
  const [page, setPage] = useState(1)
  const [rowsPerPage, setRowsPerPage] = useState(10)
  const { data: dataLocation, refetch: refetchLocationPoint, isLoading } = useListConfigWaypoints("all", {
    building_uuid: building_uuid || '',
    page,
    pageSize: rowsPerPage,
    search: searchQuery || '',
  })

  useEffect(() => {
    if (isLoading) {
      return
    }
    refetchLocationPoint()
  }, [searchQuery, page, rowsPerPage])

  const listLocations = dataLocation?.data || []
  const itemCount = dataLocation?.count || 1

  const handleChangePage = (newPage: number) => {
    setPage(newPage + 1)
  }

  const handleChangeRowsPerPage = (newRowsPerPage: number) => {
    setRowsPerPage(newRowsPerPage)
    setPage(1)
  }

  const uniqueMap = (location: LocationType, key: keyof LocationMapType) => {
    return location.map.filter((map, index) => {
      return location.map.findIndex((m) => m[key] === map[key]) === index
    })
  }

  const isDisableNext = listLocations.length === 0;

  const onHandleNext = () => {
    if (isDisableNext) {
      return
    }

    onNext?.()
  }
  return (
    <>
      <Paper elevation={2} sx={{ display: 'flex', flexDirection: 'column', maxHeight: "67vh", borderRadius: "10px", border: "1px solid #E0E0E0", m: '20px auto' }}>
        <TableContainer sx={{ alignItems: "center", overflowX: "auto" }}>
          <Table stickyHeader>
            <TableHead sx={{ width: "100%", margin: "auto" }}>
              <TableRow sx={{ width: "100%", margin: "auto" }}>
                {columns.map((column, index) => (
                  <TableCell key={index} align="center" sx={{ ...defaultHeaderStyle, textTransform: 'capitalize' }}>
                    {column}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {listLocations.map((location, index) => (
                <TableRow key={index}
                  sx={{ cursor: 'pointer', '&:hover': { backgroundColor: COLOR.gray[100] } }}
                >
                  <TableCell align="center" sx={{ ...defaultTableCellStyle }}>{location.location_name}</TableCell>
                  <TableCell align="center" sx={{ ...defaultTableCellStyle }}>
                    {uniqueMap(location, 'building').map((map, idx) => (
                      <Typography key={idx}>{map.building}</Typography>
                    ))}
                  </TableCell>
                  <TableCell align="center" sx={{ ...defaultTableCellStyle }}>
                    {uniqueMap(location, 'map_level').map((map, idx) => (
                      <Typography key={idx}>{map.map_level}</Typography>
                    ))}
                  </TableCell>
                  <TableCell align="center" sx={{ ...defaultTableCellStyle }}>
                    <Typography sx={{ textTransform: 'capitalize' }}>
                      {location.location_type.replaceAll('_', ' ')}
                    </Typography>
                  </TableCell>
                  <TableCell align="center" sx={{ ...defaultTableCellStyle }}>
                    {uniqueMap(location, 'map_level').map((map, idx) => (
                      <Typography key={idx}>{map.map_name}</Typography>
                    ))}
                  </TableCell>
                  <TableCell align="center" sx={{ ...defaultTableCellStyle }}>
                    <IconButton onClick={() => onEdit?.(location)}>
                      <EditIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
              {listLocations.length === 0 && !isLoading && (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ color: COLOR.gray[500] }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '20px' }}>
                      <Typography sx={{ fontSize: '16px', fontWeight: 500, color: COLOR.gray[500], mt: '10px' }}>No Waypoints data available</Typography>
                      <Typography sx={{ fontSize: '16px', fontWeight: 500, color: COLOR.gray[500], mt: '10px' }}>Please create new Waypoints</Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[10, 25, 50]}
          component="div"
          count={itemCount}
          rowsPerPage={rowsPerPage}
          page={page - 1}
          onPageChange={(e, num) => handleChangePage(num)}
          onRowsPerPageChange={(e) => handleChangeRowsPerPage(parseInt(e.target.value || '10'))}
          sx={{ flexShrink: 0, boxShadow: "none" }} // Prevent pagination from shrinking
        />
      </Paper>

      {showNextButton && (
        <Box sx={{ display: 'flex', justifyContent: 'end' }}>
          <Button onClick={onHandleNext} variant="contained" disabled={isDisableNext}>Next</Button>
        </Box>
      )}
    </>
  )
}